import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { 
  ChevronLeft, ChevronRight, X, CheckCircle2, 
  Circle, Play, BookOpen, Sparkles
} from 'lucide-react';

/**
 * InteractiveWalkthrough - Step-by-step guided tour component
 * 
 * Features:
 * - Highlights target elements
 * - Shows step instructions
 * - Tracks progress
 * - Supports various actions (click, type, wait, highlight)
 */
export function InteractiveWalkthrough({ 
  walkthrough,
  isActive = false,
  onComplete,
  onDismiss,
  onStepChange
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [highlightPosition, setHighlightPosition] = useState(null);

  const currentStep = walkthrough?.steps?.[currentStepIndex];
  const totalSteps = walkthrough?.steps?.length || 0;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  // Find and highlight target element
  useEffect(() => {
    if (!isActive || !currentStep?.target) return;

    const findTarget = () => {
      const element = document.querySelector(currentStep.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        const padding = currentStep.highlightPadding || 4;
        setHighlightPosition({
          top: rect.top - padding + window.scrollY,
          left: rect.left - padding,
          width: rect.width + (padding * 2),
          height: rect.height + (padding * 2)
        });

        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setHighlightPosition(null);
      }
    };

    findTarget();
    // Re-check position on scroll/resize
    const handleChange = () => findTarget();
    window.addEventListener('scroll', handleChange);
    window.addEventListener('resize', handleChange);

    return () => {
      window.removeEventListener('scroll', handleChange);
      window.removeEventListener('resize', handleChange);
    };
  }, [isActive, currentStep]);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCompletedSteps(prev => [...prev, currentStep.id]);
      setCurrentStepIndex(prev => prev + 1);
      onStepChange?.(currentStepIndex + 1);
    } else {
      // Walkthrough complete
      setCompletedSteps(prev => [...prev, currentStep.id]);
      onComplete?.(walkthrough);
    }
  }, [currentStepIndex, totalSteps, currentStep, walkthrough, onComplete, onStepChange]);

  const goToPrevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      onStepChange?.(currentStepIndex - 1);
    }
  }, [currentStepIndex, onStepChange]);

  const handleDismiss = () => {
    setCurrentStepIndex(0);
    setCompletedSteps([]);
    setHighlightPosition(null);
    onDismiss?.();
  };

  if (!isActive || !walkthrough) return null;

  // Calculate tooltip position based on step.position
  const getTooltipPosition = () => {
    if (!highlightPosition) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const position = currentStep?.position || 'bottom';
    const gap = 16;

    switch (position) {
      case 'top':
        return {
          bottom: `calc(100vh - ${highlightPosition.top}px + ${gap}px)`,
          left: highlightPosition.left + highlightPosition.width / 2,
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          top: highlightPosition.top + highlightPosition.height + gap,
          left: highlightPosition.left + highlightPosition.width / 2,
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          top: highlightPosition.top + highlightPosition.height / 2,
          right: `calc(100vw - ${highlightPosition.left}px + ${gap}px)`,
          transform: 'translateY(-50%)'
        };
      case 'right':
        return {
          top: highlightPosition.top + highlightPosition.height / 2,
          left: highlightPosition.left + highlightPosition.width + gap,
          transform: 'translateY(-50%)'
        };
      default:
        return {
          top: highlightPosition.top + highlightPosition.height + gap,
          left: highlightPosition.left + highlightPosition.width / 2,
          transform: 'translateX(-50%)'
        };
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-black/50 pointer-events-none" />

      {/* Highlight cutout */}
      {highlightPosition && (
        <div
          className="fixed z-[9999] border-2 border-emerald-500 rounded-lg pointer-events-none"
          style={{
            top: highlightPosition.top,
            left: highlightPosition.left,
            width: highlightPosition.width,
            height: highlightPosition.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 20px rgba(16, 185, 129, 0.5)'
          }}
        >
          {/* Pulse animation */}
          <div className="absolute inset-0 border-2 border-emerald-400 rounded-lg animate-ping" />
        </div>
      )}

      {/* Step tooltip */}
      <Card 
        className="fixed z-[10000] w-80 shadow-xl border-emerald-200"
        style={getTooltipPosition()}
        data-testid="walkthrough-tooltip"
      >
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <Badge variant="secondary" className="text-xs">
                  Step {currentStepIndex + 1} of {totalSteps}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleDismiss}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-100 rounded-full mb-4 overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step content */}
          <h4 className="font-semibold text-gray-900 mb-2">{currentStep?.title}</h4>
          <p className="text-sm text-gray-600 mb-4">{currentStep?.content}</p>

          {/* Action hint */}
          {currentStep?.action && currentStep.action !== 'highlight' && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 mb-4 bg-emerald-50 p-2 rounded">
              <Play className="w-3 h-3" />
              <span>
                {currentStep.action === 'click' && 'Click the highlighted element to continue'}
                {currentStep.action === 'type' && 'Type in the highlighted field to continue'}
                {currentStep.action === 'wait' && 'Wait for the action to complete'}
                {currentStep.action === 'select' && 'Make a selection to continue'}
              </span>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToPrevStep}
              disabled={currentStepIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {/* Step dots */}
            <div className="flex gap-1">
              {walkthrough.steps.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx < currentStepIndex 
                      ? 'bg-emerald-500' 
                      : idx === currentStepIndex 
                        ? 'bg-emerald-400 ring-2 ring-emerald-200'
                        : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            <Button 
              size="sm" 
              onClick={goToNextStep}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {currentStepIndex === totalSteps - 1 ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Finish
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/**
 * WalkthroughCard - Card to start a walkthrough
 */
export function WalkthroughCard({ 
  walkthrough, 
  onStart,
  completed = false 
}) {
  return (
    <Card 
      className={`hover:shadow-md transition-shadow ${completed ? 'border-emerald-200 bg-emerald-50/50' : ''}`}
      data-testid={`walkthrough-card-${walkthrough.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            completed ? 'bg-emerald-500' : 'bg-gradient-to-br from-emerald-100 to-teal-100'
          }`}>
            {completed ? (
              <CheckCircle2 className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-emerald-600" />
            )}
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">{walkthrough.title}</h4>
            <p className="text-sm text-gray-600 mb-2">{walkthrough.description}</p>
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
              <span className="flex items-center gap-1">
                <Circle className="w-3 h-3" />
                {walkthrough.steps?.length || 0} steps
              </span>
              <span>~{walkthrough.estimatedTime}</span>
            </div>
            <Button 
              size="sm" 
              onClick={() => onStart?.(walkthrough)}
              className={completed ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : ''}
            >
              {completed ? (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Redo
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Start Tour
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * WalkthroughCompletionModal - Shown when walkthrough is finished
 */
export function WalkthroughCompletionModal({ 
  walkthrough, 
  isOpen, 
  onClose,
  onViewDocs 
}) {
  if (!isOpen || !walkthrough) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Walkthrough Complete!</h3>
          <p className="text-gray-600 mb-6">{walkthrough.completionMessage}</p>
          
          <div className="flex flex-col gap-2">
            {walkthrough.relatedDocs?.length > 0 && (
              <Button variant="outline" onClick={() => onViewDocs?.(walkthrough.relatedDocs[0])}>
                <BookOpen className="w-4 h-4 mr-2" />
                View Documentation
              </Button>
            )}
            <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700">
              Got it!
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default InteractiveWalkthrough;
